import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { deleteTool, getTools, Tool } from "../../api/tooling";
import { HasRole } from "../../context/HasRole";
import CustomTable from "../common/CustomTable";
import { ColumnDef } from "@tanstack/react-table";
import ToolForm from "./ToolForm";
import ToolHistory from "./ToolHistory";
import { DeleteCross, ErrorNotice } from "./ToolingShared";
import "../common/kanban/kanban.css";

export default function ToolingsDashboard() {
  const tools = useQuery({
    queryKey: ["tooling", "tools", "including-deleted"],
    queryFn: () => getTools(true),
  });
  const [editing, setEditing] = useState<Tool | null | undefined>();
  const [history, setHistory] = useState<Tool>();
  const client = useQueryClient();
  const deletion = useMutation({
    mutationFn: deleteTool,
    onSuccess: () => client.invalidateQueries({ queryKey: ["tooling"] }),
  });
  const columns = useMemo<ColumnDef<Tool>[]>(
    () => [
      { accessorKey: "id", header: "Id", enableColumnFilter: false },
      {
        accessorKey: "type",
        header: "Type",
        cell: (info) => (
          <span className="text-capitalize">
            {String(info.getValue())} tool
          </span>
        ),
      },
      {
        id: "parts",
        header: "Parts / Part IDs",
        accessorFn: (tool) =>
          tool.parts.map((p) => `${p.part_name} (${p.part_id})`).join(", "),
      },
      { accessorKey: "operation", header: "Operation" },
      { accessorKey: "code", header: "Tool code" },
      {
        id: "location",
        header: "Location",
        accessorFn: (tool) => tool.location?.label ?? "—",
      },
      { accessorKey: "details", header: "Details" },
      {
        id: "state",
        header: "Status",
        accessorFn: (tool) => (tool.is_deleted ? "Deleted" : "Active"),
        cell: ({ row }) => (
          <span
            className={`tooling-record-status ${
              row.original.is_deleted ? "is-deleted" : "is-active"
            }`}
          >
            {row.original.is_deleted ? "Deleted" : "Active"}
          </span>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => (
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => setHistory(row.original)}
            >
              History
            </button>
            <HasRole roles={["admin", "accounts", "production"]}>
              {!row.original.is_deleted && (
                <>
                  <button
                    className="btn btn-sm btn-action"
                    onClick={() => setEditing(row.original)}
                  >
                    Edit
                  </button>
                  <button
                    className="btn btn-sm tooling-delete-cross"
                    title="Delete tooling"
                    aria-label={`Delete tooling ${row.original.code}`}
                    disabled={deletion.isLoading}
                    onClick={() => {
                      if (
                        window.confirm(
                          `Soft delete tooling ${row.original.code}?`
                        )
                      )
                        deletion.mutate(row.original.id);
                    }}
                  >
                    <DeleteCross />
                  </button>
                </>
              )}
            </HasRole>
          </div>
        ),
      },
    ],
    [deletion.isLoading, deletion.mutate]
  );
  return (
    <section className="toolings-dashboard" aria-label="Toolings">
      <div className="tooling-toolbar">
        <h4 className="mb-0">Toolings</h4>
        <HasRole roles={["admin", "accounts", "production"]}>
          <button className="styled-btn--std" onClick={() => setEditing(null)}>
            + Add Tooling
          </button>
        </HasRole>
      </div>
      <ErrorNotice error={tools.error} retry={() => tools.refetch()} />
      <ErrorNotice error={deletion.error} />
      {tools.isLoading ? (
        <p>Loading tools…</p>
      ) : (
        tools.data && (
          <CustomTable
            data={tools.data}
            columns={columns}
            isFilter
            isPaginate
            rowClassName={(tool: Tool) =>
              tool.is_deleted ? "tooling-deleted-row" : ""
            }
          />
        )
      )}
      {tools.data?.length === 0 && (
        <p>
          No tools yet. Add a tooling and link its inventory parts to get
          started.
        </p>
      )}
      {editing !== undefined && (
        <ToolForm
          tool={editing || undefined}
          onClose={() => setEditing(undefined)}
        />
      )}
      {history && (
        <ToolHistory tool={history} onClose={() => setHistory(undefined)} />
      )}
    </section>
  );
}
